import { useState, useEffect, useRef, useCallback } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import AuthWrapper from './components/Auth/AuthWrapper';
import ChatInterface from './components/Chat/ChatInterface';
import { MessageInputHandle } from './components/Chat/MessageInput';
import Sidebar from './components/UI/Sidebar';
import { useToast } from './components/UI/Toast';
import { documentService } from './services/documentService';
import { sessionDocumentService } from './services/sessionDocumentService';
import { chatService } from './services/chatService';
import { groundingService } from './services/groundingService';
import { StorageManager } from './utils/storageManager';
import { logger } from './utils/logger';
import { buildActiveContext } from './utils/activeContext';
import { useSessionDocuments } from './utils/constants';
import {
  isStoredChatSessionArray,
  isStoredMessageArray,
} from './types';
import type { Message, Document as AppDocument } from './types';
import './App.css';

// Custom hook for responsive design
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return isMobile;
};

interface ChatSession {
  id: string;
  title: string;
  messages: Message[];
  timestamp: Date;
  messageCount: number;
}

function App() {
  const isAuthenticated = useIsAuthenticated();
  const isMobile = useIsMobile();
  const toast = useToast();
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const [documents, setDocuments] = useState<AppDocument[]>([]);
  const inputRef = useRef<MessageInputHandle>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Load conversation sessions only after authentication
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const loadConversationSessions = async () => {
      try {
        // Check storage availability and quota
        if (!StorageManager.isAvailable()) {
          console.warn('localStorage not available (private mode?)');
        } else {
          const quota = await StorageManager.checkQuota();
          if (!quota.available) {
            console.warn('Storage quota low, cleaning up old sessions...');
            StorageManager.clearOldSessions(10);
          }
        }

        // First, try to load from localStorage. safeGetJSON validates the
        // shape and clears the key if it's corrupted, so a bad payload no
        // longer crashes the entire app on mount.
        const parsedSessions = StorageManager.safeGetJSON('chatSessions', isStoredChatSessionArray);
        if (parsedSessions) {
          setChatSessions(parsedSessions.map((s) => ({
            id: s.id,
            title: s.title,
            messages: [],
            timestamp: new Date(s.timestamp),
            messageCount: s.messageCount,
          })));
          logger.debug('Loaded sessions from localStorage:', parsedSessions.length);
        }
        
        // Try to fetch from backend (will fail gracefully if Azure Function is down)
        const sessions = await chatService.getConversationSessions();
        if (Array.isArray(sessions) && sessions.length > 0) {
          const convertedSessions: ChatSession[] = sessions.map(session => ({
            id: session.id,
            title: session.title,
            messages: [],
            timestamp: new Date(session.lastMessageTime),
            messageCount: session.messageCount
          }));
          setChatSessions(convertedSessions);
          StorageManager.safeSetItem('chatSessions', JSON.stringify(convertedSessions));
          logger.debug('Loaded sessions from backend:', sessions.length);
        }
        
        // Try to restore currentChatId from localStorage
        const savedChatId = localStorage.getItem('currentChatId');
        if (savedChatId) {
          setCurrentChatId(savedChatId);
        } else {
          setMessages([]);
          setCurrentChatId(null);
        }
      } catch (error) {
        console.error('Failed to load conversation sessions:', error);
        // Don't clear sessions on error - keep whatever we have from localStorage
      }
    };
    
    loadConversationSessions();
  }, [isAuthenticated]);

  // Load documents on app start or when conversation changes
  const loadDocuments = async (conversationId?: string) => {
    try {
      console.log('Loading documents for conversation:', conversationId);
      const docs = useSessionDocuments()
        ? await sessionDocumentService.getDocuments(conversationId)
        : await documentService.getDocuments(conversationId);
      console.log('Loaded documents:', docs.length);
      setDocuments(docs);
    } catch (error) {
      console.error('Failed to load documents:', error);
      setDocuments([]);
    }
  };

  // Save currentChatId to localStorage and load conversation-specific documents
  useEffect(() => {
    if (currentChatId) {
      localStorage.setItem('currentChatId', currentChatId);
      loadDocuments(currentChatId);
    } else {
      localStorage.removeItem('currentChatId');
      setDocuments([]); // Clear documents when no conversation is active
    }
  }, [currentChatId]);


  const handleSendMessage = async (content: string) => {
    const userMessage: Message = {
      id: Date.now().toString(),
      content,
      isUser: true,
      sender: 'user',
      timestamp: new Date(),
    };

    // Check if this is an existing chat continuation
    const isExistingChat = currentChatId && 
      (messages.length > 0 || documents.length > 0);

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      let conversationId = currentChatId;
      
      if (!isExistingChat) {
        conversationId = `conv_user-1_${Date.now()}`;
        setCurrentChatId(conversationId);
        console.log('Starting new conversation with ID:', conversationId);
      } else {
        console.log('Continuing existing conversation:', conversationId);
      }

      // Build conversation history for context window
      const conversationHistory = messages.map(msg => ({
        role: msg.isUser ? 'user' : 'assistant',
        content: msg.content
      }));
      // Add current user message to history
      conversationHistory.push({ role: 'user', content });

      const sessionDocumentsEnabled = useSessionDocuments();
      const activeContext = buildActiveContext(messages, documents, content);
      const sessionDocumentIds = sessionDocumentsEnabled
        ? documents
            .filter((doc) => doc.sessionDocument && doc.conversationId === conversationId && doc.status !== 'failed')
            .map((doc) => doc.id)
        : [];

      // The backend-owned session document path sends document IDs only. Do
      // not also attach legacy localStorage excerpts or the model can answer
      // from stale partial context.
      const documentContents = conversationId && !sessionDocumentsEnabled
        ? documentService.getRelevantConversationDocumentContents(conversationId, content, activeContext)
        : [];
      
      if (documentContents.length > 0 || sessionDocumentIds.length > 0) {
        logger.debug('Including document context in query:', {
          excerptCount: documentContents.length,
          sessionDocumentCount: sessionDocumentIds.length,
        });
      }

      // Set up an abort controller so the user can stop the in-flight
      // grounding round-trip via the stop button (replaces the previous
      // "wait 30s for completion" UX).
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      // Use grounding service for AI responses with citations (pass conversation history and documents)
      const groundingResult = await groundingService.getGroundedResponse(
        content,
        conversationId || undefined,
        conversationHistory,
        documentContents,
        activeContext,
        sessionDocumentIds,
        controller.signal
      );
      
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: groundingResult.answer,
        isUser: false,
        sender: 'ai',
        timestamp: new Date(),
        citations: groundingResult.citations,
        groundingSource: groundingResult.groundingSource,
        threadSummaries: groundingResult.threadSummaries,
        suggestedApp: groundingResult.suggestedApp ?? null,
      };

      // Create the full updated messages array BEFORE updating state
      const updatedMessages = [...messages, userMessage, assistantMessage];
      
      setMessages(updatedMessages);

      // Scroll to the assistant message
      setTimeout(() => {
        const chatContent = document.querySelector('.chat-content');
        if (chatContent) {
          chatContent.scrollTop = chatContent.scrollHeight;
        }
      }, 100);

      // Save messages to localStorage for this conversation (includes groundingSource and citations)
      if (conversationId) {
        localStorage.setItem(`chat_${conversationId}`, JSON.stringify(updatedMessages));
        console.log('Saved chat messages to localStorage:', conversationId, 'with groundingSource:', assistantMessage.groundingSource);
        
        // Also save to Azure Function for persistence
        const historyToSave = [...conversationHistory, { role: 'assistant', content: groundingResult.answer }];
        groundingService.saveConversation(
          'user-1', // Will be replaced with actual user ID from auth
          conversationId,
          historyToSave
        ).catch(err => console.warn('Failed to save to backend:', err));
      }

      // Update chat sessions
      if (!isExistingChat) {
        console.log('Creating new local session for conversation');
        const newSession: ChatSession = {
          id: conversationId!,
          title: content.length > 50 ? content.substring(0, 50) + '...' : content,
          messages: [],
          timestamp: new Date(),
          messageCount: 2
        };
        setChatSessions(prev => {
          const updated = [newSession, ...prev];
          localStorage.setItem('chatSessions', JSON.stringify(updated));
          return updated;
        });
      } else {
        console.log('Updating existing chat locally');
        setChatSessions(prev => {
          const updated = prev.map(session => {
            if (session.id === currentChatId) {
              return {
                ...session,
                messageCount: session.messageCount + 2,
                timestamp: new Date()
              };
            }
            return session;
          });
          
          const updatedSession = updated.find(s => s.id === currentChatId);
          const otherSessions = updated.filter(s => s.id !== currentChatId);
          const final = updatedSession ? [updatedSession, ...otherSessions] : updated;
          localStorage.setItem('chatSessions', JSON.stringify(final));
          return final;
        });
      }
    } catch (error) {
      const wasAborted = error instanceof DOMException && error.name === 'AbortError';
      if (wasAborted) {
        logger.debug('Generation stopped by user');
      } else {
        logger.error('Failed to send message:', error);
        toast.error('Failed to send message. Please try again.');
      }
      setMessages(prev => prev.slice(0, -1)); // Remove user message on error
    } finally {
      abortControllerRef.current = null;
      setIsLoading(false);
    }
  };

  // Routing ledger: record which app a user actually opened from a handoff
  // card. Paired with the suggestedApp logged at classification time, this is
  // how chat-driven app engagement gets measured per app.
  const handleAppHandoff = useCallback((appKey: string) => {
    void groundingService.logAppHandoff(appKey);
  }, []);

  const handleStopGeneration = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setIsLoading(false);
  }, []);

  const handleNewChat = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setMessages([]);
    setCurrentChatId(null);
    setDocuments([]); // Clear documents when starting new chat
  }, []);

  // Keyboard shortcuts: Cmd/Ctrl+K starts a new chat, Cmd/Ctrl+/ focuses
  // the message input. ESC inside dialogs is handled by the Modal component.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      if (e.key.toLowerCase() === 'k') {
        e.preventDefault();
        handleNewChat();
      } else if (e.key === '/') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleNewChat]);

  const handleSelectChat = async (chatId: string) => {
    try {
      setIsLoading(true);
      
      // Find the session from local state first (since we store in localStorage)
      const localSession = chatSessions.find(session => session.id === chatId);
      
      if (!localSession) {
        console.warn('Session not found in local state:', chatId);
        return;
      }
      
      // Always load from localStorage first (it has full message data including
      // groundingSource and citations). safeGetJSON validates and clears bad
      // payloads instead of crashing.
      const parsedMessages = StorageManager.safeGetJSON(`chat_${chatId}`, isStoredMessageArray);
      if (parsedMessages) {
        setMessages(parsedMessages.map((m) => ({
          id: m.id,
          content: m.content,
          isUser: m.isUser ?? m.sender === 'user',
          sender: m.sender ?? (m.isUser ? 'user' : 'ai'),
          timestamp: new Date(m.timestamp),
          citations: m.citations,
          groundingSource: m.groundingSource,
          threadSummaries: m.threadSummaries,
          // Rehydrated suggestions are re-checked against current AD groups by
          // AppHandoffCard before anything renders.
          suggestedApp: m.suggestedApp ?? null,
        })));
        setCurrentChatId(chatId);
        logger.debug('Loaded chat history from localStorage:', parsedMessages.length, 'messages');
      } else {
        // Fallback: Try to load from backend (won't have groundingSource/citations)
        try {
          const history = await chatService.getConversationHistoryBySession(chatId);
          
          if (history && history.length > 0) {
            const convertedMessages: Message[] = history.map((entry, index) => ({
              id: `${chatId}-${index}`,
              content: entry.content,
              isUser: entry.role === 'user',
              sender: entry.role === 'user' ? 'user' : 'ai',
              timestamp: new Date()
            }));
            
            setMessages(convertedMessages);
            setCurrentChatId(chatId);
            console.log('Loaded chat history from backend:', convertedMessages.length, 'messages');
            return;
          }
        } catch (error) {
          console.warn('Backend history unavailable:', error);
        }
        
        // If no history found anywhere, at least switch to this chat
        setMessages([]);
        setCurrentChatId(chatId);
        console.log('No history found for chat, starting fresh');
      }
    } catch (error) {
      console.error('Failed to load chat session:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteChat = async (sessionId: string) => {
    try {
      // Try to delete from backend (best effort)
      try {
        await chatService.deleteConversationSession(sessionId);
      } catch (backendError) {
        console.warn('Backend delete failed, continuing with local delete:', backendError);
      }
      
      // Remove from local state and localStorage
      setChatSessions(prev => {
        const updated = prev.filter(session => session.id !== sessionId);
        localStorage.setItem('chatSessions', JSON.stringify(updated));
        return updated;
      });
      
      // If this was the current chat, clear it
      if (currentChatId === sessionId) {
        setMessages([]);
        setCurrentChatId(null);
        localStorage.removeItem('currentChatId');
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    }
  };

  // Document upload using localStorage
  const handleFileUpload = async (file: File) => {
    try {
      // If no conversation exists, create one first so the document is associated with it
      let conversationId = currentChatId;
      if (!conversationId) {
        conversationId = `conv_user-1_${Date.now()}`;
        setCurrentChatId(conversationId);
        console.log('Created new conversation for document upload:', conversationId);
        
        // Also create a session entry so it shows in recent chats
        const newSession: ChatSession = {
          id: conversationId,
          title: `Document: ${file.name}`,
          messages: [],
          timestamp: new Date(),
          messageCount: 0,
        };
        setChatSessions(prev => [newSession, ...prev]);
        
        // Save to localStorage
        const stored = localStorage.getItem('chatSessions');
        const sessions = stored ? JSON.parse(stored) : [];
        sessions.unshift({ id: conversationId, title: newSession.title, timestamp: newSession.timestamp });
        localStorage.setItem('chatSessions', JSON.stringify(sessions));
      }
      
      const doc = useSessionDocuments()
        ? await sessionDocumentService.uploadDocument(file, conversationId)
        : await documentService.uploadDocument(file, conversationId);
      setDocuments(prev => [...prev, doc]);
      logger.debug('Document uploaded:', doc.fileName, 'to conversation:', conversationId);
    } catch (error) {
      logger.error('Failed to upload document:', error);
      toast.error('Failed to upload document. Please try again.');
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    try {
      const doc = documents.find((item) => item.id === documentId);
      if (useSessionDocuments() && doc?.sessionDocument && doc.conversationId) {
        await sessionDocumentService.deleteDocument(documentId, doc.conversationId);
      } else {
        await documentService.deleteDocument(documentId);
      }
      setDocuments(prev => prev.filter(d => d.id !== documentId));
      console.log('Document deleted:', documentId);
    } catch (error) {
      console.error('Failed to delete document:', error);
    }
  };


  return (
    <AuthWrapper>
      <div className="app">
        {!isMobile && (
          <Sidebar
            onNewChat={handleNewChat}
            chatSessions={chatSessions}
            onSelectChat={handleSelectChat}
            onDeleteChat={handleDeleteChat}
            currentChatId={currentChatId}
          />
        )}
        <main className="main-content">
          <ChatInterface
            messages={messages}
            isLoading={isLoading}
            onSendMessage={handleSendMessage}
            onFileUpload={handleFileUpload}
            onStopGeneration={handleStopGeneration}
            inputRef={inputRef}
            documents={documents}
            onDeleteDocument={handleDeleteDocument}
            chatSessions={chatSessions}
            onNewChat={handleNewChat}
            onSelectChat={handleSelectChat}
            onDeleteChat={handleDeleteChat}
            currentChatId={currentChatId}
            onAppHandoff={handleAppHandoff}
          />
        </main>
      </div>
    </AuthWrapper>
  );
}

export default App;
